import { useEffect, useState } from "react";

import { kaiApi } from "@/services/kaiApi";
import { KaiScheduleSnapshot } from "./KaiScheduleSnapshot";
import { parseScheduleSnapshotSpec } from "./kaiScheduleTypes";
import { KaiBlockLoading } from "./KaiBlockLoading";
import { KaiBlockError } from "./KaiBlockError";

type KaiScheduleSnapshotSpec = NonNullable<
  ReturnType<typeof parseScheduleSnapshotSpec>
>;

/**
 * Resolves a DB-backed `schedule_snapshot` artifact by ID. During a live turn
 * the SSE payload may include the full card under `artifact`; on reload we
 * fetch it.
 */
export function KaiScheduleSnapshotRef({
  artifactId,
  conversationId,
  cachedArtifact,
}: {
  artifactId: string;
  conversationId?: string | null;
  cachedArtifact?: unknown;
}) {
  const cachedSpec = cachedArtifact
    ? parseScheduleSnapshotSpec(JSON.stringify(cachedArtifact))
    : null;

  const [spec, setSpec] = useState<KaiScheduleSnapshotSpec | null>(cachedSpec);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (spec || !conversationId || !artifactId) return;
    let cancelled = false;
    (async () => {
      try {
        const artifact = await kaiApi.getArtifact(conversationId, artifactId);
        const parsed = parseScheduleSnapshotSpec(
          JSON.stringify(artifact.payload),
        );
        if (cancelled) return;
        if (parsed) setSpec(parsed);
        else setFailed(true);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [spec, conversationId, artifactId]);

  if (spec) return <KaiScheduleSnapshot spec={spec} />;
  if (failed) return <KaiBlockError tag="schedule_snapshot" />;
  return <KaiBlockLoading label="Loading schedule…" />;
}
